import { FC, useState, useEffect } from 'react'
import { ExpandedState, useReactTable, getCoreRowModel, getExpandedRowModel, flexRender } from '@tanstack/react-table'
import { Checkbox } from 'mk-pc'
import { getNavList, getNavGrantList } from '@/features/nav'
import { DataScopeSet } from './DataScopeSet'
import { TableColumn } from './Column'
import './index.scss'

interface Props {
  functions?: Array<any>
  dataScopes?: Array<any>
  onSelection?: (value: boolean, keys: Array<string>) => void
  onFunctionChange?: (e: any) => void
  onDataScopeChange?: (e: any) => void
}

/**
 * 生成导航树
 * @param navs
 * @param permissions
 * @param pid
 * @returns
 */
const genNavTree = (navs: Array<any>, pid: string = '') => {
  const children = [] as Array<any>

  navs
    .filter((n: any) => {
      return n.pid === pid
    })
    .forEach((n: any) => {
      const _children = genNavTree(navs, n.id)
      const _child = {
        ...n,
        id: n.id,
        pid: parent ? pid : n.id,
        pids: n.idPath
      }

      if (Array.isArray(_children) && _children.length > 0) {
        _child.children = _children
      }

      children.push(_child)
    })

  return children
}

/**
 * 授权表格
 * @param props
 * @returns
 */
export const GrantTable: FC<Props> = (props) => {
  const { functions = [], dataScopes = [], onSelection, onFunctionChange, onDataScopeChange } = props
  const [expanded, setExpanded] = useState<ExpandedState>({})
  const [rowSelection, setRowSelection] = useState({})
  const [dataScopeVisible, setDataScopeVisible] = useState<boolean>(false)
  const [currentNav, setCurrentNav] = useState<any>(null)

  const [navs, setNavs] = useState<any>([])

  useEffect(() => {
    fetchNav()
  }, [])

  /**
   * 获取导航数据
   */
  const fetchNav = async () => {
    const [_navs, _grants] = await Promise.all([getNavList(), getNavGrantList()])

    const { functions = [], dataScopes = [] } = _grants

    const navs = _navs.map((n: any) => {
      n.functions = functions.filter((f: any) => f.navId === n.id)
      n.dataScopes = dataScopes.filter((ds: any) => ds.navId === n.id)

      return n
    })
    setNavs(genNavTree(navs))
  }

  /**
   *  表格列
   */
  const columns = [
    {
      accessorKey: 'name',
      header: '导航名称',
      cell: ({ row, getValue }: any) => <TableColumn row={row} getValue={getValue} onChange={onCheckedChange} />
    },
    {
      id: 'functions',
      accessorKey: 'functions',
      cell: ({ row, getValue }: any) => {
        const rowFunctions = getValue()

        return (
          <div className="grantable-table__cell">
            {(rowFunctions || []).map((item: any) => {
              return (
                <div key={item.functionCode}>
                  <Checkbox
                    checked={validateFunctionChecked(item)}
                    value={item.functionCode}
                    onChange={(e: any) => handleFunctionChange(row, item, e)}
                    style={{ marginRight: 5 }}
                  />
                  {item.functionName}
                </div>
              )
            })}
          </div>
        )
      },
      header: '操作权限'
    },
    {
      id: 'dataScopes',
      accessorKey: 'dataScopes',
      cell: ({ row, getValue }: any) => {
        const data = getValue()
        if (Array.isArray(data) && data.length > 0) {
          return (
            <span
              className="scope-link"
              onClick={() => {
                setCurrentNav(row?.original)
                setDataScopeVisible(true)
              }}
            >
              设置数据权限
            </span>
          )
        } else {
          return null
        }
      },
      header: '数据范围'
    }
  ]

  const table = useReactTable({
    data: navs,
    columns,
    state: {
      expanded,
      rowSelection
    },
    getRowId: (originalRow) => originalRow.id,
    onExpandedChange: setExpanded,
    getSubRows: (row: any) => row.children,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onRowSelectionChange: setRowSelection,
    debugTable: false
  })

  /**
   * 监听table，进行默认全部展开
   */
  useEffect(() => {
    table.toggleAllRowsExpanded()
  }, [table])

  /**
   * 监听默认选中状态
   */
  useEffect(() => {
    const selected: { [key: string]: boolean } = {}
    functions.forEach((f: any) => {
      if (f.resourceType === 'MENU') {
        selected[f.resourceId] = true
      }
    })
    table.setRowSelection(selected)
  }, [functions])

  /**
   * 监听选中状态
   */
  useEffect(() => {
    if (!table) return

    const { rows } = table.getRowModel()

    const ids: { [key: string]: boolean } = {}

    rows.forEach((row) => {
      if (row.subRows.length > 0) {
        if (row.getIsAllSubRowsSelected()) {
          if (!row.getIsSelected()) {
            ids[row.id] = true
          }
        } else {
          if (row.getIsSelected()) {
            ids[row.id] = false
          }
        }
      }
    })

    const keys = Object.keys(ids)
    if (keys.length > 0) {
      setRowSelection((state) => {
        const newState: { [key: string]: boolean } = { ...state }
        Object.keys(ids).forEach((id) => {
          if (ids[id]) {
            newState[id] = true
          } else {
            delete newState[id]
          }
        })
        return newState
      })
    }
  }, [rowSelection])

  /**
   * 监听复选框状态
   */
  const onCheckedChange = (value: boolean, row: any) => {
    const keys = []
    keys.push(row.id)

    row.getLeafRows().forEach((child: any) => {
      keys.push(child.id)
    })

    onSelection && onSelection(value, keys)
  }

  /**
   * 验证权限点是否勾选
   * @param item
   * @returns
   */
  const validateFunctionChecked = (item: any) => {
    const _index = (functions || []).findIndex(
      (rf: any) => rf.resourceType === 'BUTTON' && rf.resourceId === item.id && rf.resourceCode === item.functionCode
    )
    return _index >= 0
  }

  /**
   * 勾选操作权限
   * @param item
   */
  const handleFunctionChange = (item: any, functionItem: any, e: any) => {
    if (onFunctionChange) {
      onFunctionChange({
        navId: functionItem.navId,
        functionId: functionItem.id,
        functionCode: functionItem.functionCode,
        functionType: functionItem.functionType,
        functionValue: '',
        valueType: e.target.checked
      })
    }
  }

  return (
    <div className="grantable">
      <table className="grantable-table">
        <thead>
          {table.getHeaderGroups().map((headerGroup: any) => (
            <tr className="grantable-table__row" key={headerGroup.id}>
              {headerGroup.headers.map((header: any) => {
                return (
                  <th key={header.id} style={{ width: header.width || 'auto' }} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : <>{flexRender(header.column.columnDef.header, header.getContext())}</>}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row: any) => {
            return (
              <tr key={row.id} className="grantable-table__row">
                {row.getVisibleCells().map((cell: any) => {
                  return <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
      <DataScopeSet
        visible={dataScopeVisible}
        nav={currentNav}
        dataScopes={dataScopes}
        onConfirm={(e) => {
          if (onDataScopeChange) {
            onDataScopeChange(e)
          }
          setDataScopeVisible(false)
        }}
        onClose={() => setDataScopeVisible(false)}
      />
    </div>
  )
}
